import React, { FC, memo, useState } from 'react';
import styled from '@emotion/styled';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import useModal from '~ComponentsImproved/Modal/useModal';
import MessageInput from '~ComponentsImproved/MessageInput';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import MapIcon from '~Assets/icons/map.svg';
import { useButton, useI18n } from '~Hooks';
import { Box, Button, GreenButton } from '~Styles';

/** Same highlight box as PromoCodeDiscount, tinted with the light POS Classic blue so the text stays readable on the dark modal */
const InfoBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorPOSClassic11) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorPOSClassic11) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
`;

interface Props {
  isHandy: boolean;
  /** Number of settings that will be stored in the blueprint */
  settingsCount: number;
  /** Name of an existing blueprint with identical settings; saving is disabled when set */
  duplicateName?: string;
  /** Called with the chosen name once the user confirms in the name input */
  onCreate: (name: string) => void;
}

/**
 * The "Create" trigger and its modal, like PromoCodeDiscount. Create opens a
 * MessageInput stacked on top of the summary modal. OK hands the name to the
 * parent and closes both; tapping outside the input only closes the input.
 */
const CreateBlueprint: FC<Props> = ({ isHandy, settingsCount, duplicateName, onCreate }) => {
  const { i18n } = useI18n();
  const modal = useModal();
  const [askForName, setAskForName] = useState(false);

  const openButton = useButton({
    onClick: () => {
      setAskForName(false);
      modal.show();
    },
    disabled: !!duplicateName,
    disabledToast: duplicateName
      ? i18n('Settings.Blueprints.Create.Duplicate', duplicateName)
      : undefined,
  });
  const createButton = useButton({ onClick: () => setAskForName(true) });
  const cancelButton = useButton({ onClick: modal.hide });

  const submitName = (name: string) => {
    setAskForName(false);
    modal.hide();
    onCreate(name.trim());
  };

  return (
    <div>
      <Paragraph level={isHandy ? 5 : 4} color="var(--colorBlueGray200)" bottom={isHandy ? 8 : 16}>
        {i18n('Settings.Blueprints.Create.Description')}
      </Paragraph>
      <Button {...openButton} size={isHandy ? 'medium' : 'large'}>
        {i18n('Settings.Blueprints.Create.Button')}
      </Button>

      <Modal isShowing={modal.isShowing} hide={modal.hide}>
        <ModalWrapper
          padding={isHandy ? 'large' : '3xl'}
          width={isHandy ? '340px' : '480px'}
          gap="small"
          header={{
            icon: MapIcon,
            title: i18n('Settings.Blueprints.Create.Title'),
            subtitle: i18n('Settings.Blueprints.CreateBlock'),
          }}>
          <InfoBox fluid display="flex" alignItems="center" justifyContent="center">
            <Paragraph level={4} color="var(--colorPOSClassic11)">
              {i18n('Settings.Blueprints.SettingsCount', settingsCount)}
            </Paragraph>
          </InfoBox>

          <Box fluid display="flex" gap="xs" marginTop="small">
            <Button fluid {...cancelButton}>
              {i18n('General.Cancel')}
            </Button>
            <GreenButton fluid {...createButton}>
              {i18n('General.Create')}
            </GreenButton>
          </Box>
        </ModalWrapper>
      </Modal>

      {/* Stacked on top of the summary modal; tapping outside only closes the name input */}
      <Modal isShowing={askForName} hide={() => setAskForName(false)} zIndex={100}>
        <MessageInput
          type="text"
          placeHolder={i18n('Settings.Blueprints.Create.NamePlaceholder')}
          onSubmit={submitName}
        />
      </Modal>
    </div>
  );
};

export default memo(CreateBlueprint);
